// GroupManager.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import GroupControlModal from './GroupControlModal';
import CachedGridThumbnail from './CachedGridThumbnail';
import { canAccessCap } from '../permissions';
import './GroupManager.css';

const GroupManager = ({ user, onClose }) => {
  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    return fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });
  }, []);

  // 2026-05-18 (re-applied) — gate via the new hierarchical access tree.
  // pro_features column was dropped in Phase E; canAccessCap covers admin
  // bypass + scope.enabled + cap.enabled + expiry, reading from user.access.
  const hasAccess = canAccessCap(user, 'tv', 'groups');
  
  // State
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupFiles, setGroupFiles] = useState([]);
  const [groupFolders, setGroupFolders] = useState([]);
  const [targetFolder, setTargetFolder] = useState('');
  const [pendingTargetFolder, setPendingTargetFolder] = useState('');
  const [selectedUser, setSelectedUser] = useState(user.username);
  const [allUsers, setAllUsers] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState('');
  const [modalFormData, setModalFormData] = useState({ name: '', description: '', devices: [] });
  const [showGroupControlModal, setShowGroupControlModal] = useState(false);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const isAdmin = user?.is_admin === true || user?.role === 'admin';
  const selectableUsers = allUsers.filter(u => u.username !== user.username);

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const url = isAdmin && selectedUser !== user.username
        ? `/api/groups?username=${selectedUser}`
        : '/api/groups';
      
      const response = await authFetch(url);
      
      if (!response.ok) {
        const msg = response.status === 403 ? 'Access denied' : 'Failed to load groups';
        throw new Error(msg);
      }

      const data = await response.json();
      setGroups(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError(err?.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [authFetch, isAdmin, selectedUser, user.username]);

  // Fetch all users (admin only)
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      const response = await authFetch('/api/admin/users');
      
      if (!response.ok) throw new Error('Failed to fetch users');
      
      const data = await response.json();
      setAllUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, [authFetch, isAdmin]);

  // Fetch available devices for user
  const fetchDevices = useCallback(async () => {
    try {
      const response = await authFetch('/api/devices');
      
      if (!response.ok) throw new Error('Failed to fetch devices');
      
      const data = await response.json();
      
      // Filter devices by selected user
      const userDevices = data.filter(d => d.owner_username === selectedUser);
      setAvailableDevices(userDevices);
    } catch (err) {
      console.error('Error fetching devices:', err);
    }
  }, [authFetch, selectedUser]);

  // Fetch group files
  const fetchGroupFiles = useCallback(async (groupId, folder = '') => {
    try {
      setLoading(true);
      const qs = folder ? `?folder=${encodeURIComponent(folder)}` : '';
      const response = await authFetch(`/api/groups/${groupId}/files${qs}`);
      
      if (!response.ok) throw new Error('Failed to fetch group files');
      
      const data = await response.json();
      setGroupFiles(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching group files:', err);
      setError('Failed to load group files');
      setGroupFiles([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const fetchGroupFolders = useCallback(async (groupId) => {
    try {
      const response = await authFetch(`/api/groups/${groupId}/folders`);

      if (!response.ok) throw new Error('Failed to fetch group folders');

      const data = await response.json();
      setGroupFolders(Array.isArray(data?.folders) ? data.folders : []);
    } catch (err) {
      console.error('Error fetching group folders:', err);
      setGroupFolders([]);
    }
  }, [authFetch]);

  // Load initial data
  useEffect(() => {
    fetchGroups();
    fetchUsers();
    fetchDevices();
  }, [fetchGroups, fetchUsers, fetchDevices]);

  useEffect(() => {
    if (!isAdmin) {
      setSelectedUser(user.username);
    }
  }, [isAdmin, user.username]);

  // When selected user changes
  useEffect(() => {
    setSelectedGroup(null);
    setGroupFiles([]);
    fetchGroups();
    fetchDevices();
  }, [selectedUser, fetchGroups, fetchDevices]);

  // When group is selected
  useEffect(() => {
    if (selectedGroup) {
      fetchGroupFolders(selectedGroup.id);
      setTargetFolder('');
      setPendingTargetFolder('');
      setSelectedFiles([]);
    } else {
      setGroupFiles([]);
      setGroupFolders([]);
      setTargetFolder('');
      setPendingTargetFolder('');
      setSelectedFiles([]);
    }
  }, [selectedGroup, fetchGroupFolders, fetchGroupFiles]);

  useEffect(() => {
    if (!selectedGroup) return;
    fetchGroupFiles(selectedGroup.id, targetFolder);
  }, [selectedGroup, targetFolder, fetchGroupFiles]);

  // When modal opens, initialize form data
  useEffect(() => {
    if (showCreateModal) {
      setModalFormData({ name: '', description: '', devices: [] });
    } else if (showEditModal && selectedGroup) {
      setModalFormData({
        name: selectedGroup.name,
        description: selectedGroup.description || '',
        devices: selectedGroup.devices || []
      });
    }
  }, [showCreateModal, showEditModal, selectedGroup]);

  // Select group
  const handleGroupClick = (group) => {
    setSelectedGroup(group);
    setSelectedFiles([]);
  };

  const refreshAll = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchGroups(),
        fetchDevices(),
        isAdmin ? fetchUsers() : Promise.resolve()
      ]);

      if (selectedGroup?.id) {
        try {
          const response = await authFetch(`/api/groups/${selectedGroup.id}`);
          if (response.ok) {
            const details = await response.json();
            setSelectedGroup(details);
          }
        } catch (e) {
          // ignore
        }

        await Promise.all([
          fetchGroupFolders(selectedGroup.id),
          fetchGroupFiles(selectedGroup.id, targetFolder)
        ]);
      }
    } catch (err) {
      console.error('Refresh all failed:', err);
      setError('Refresh failed');
    } finally {
      setLoading(false);
    }
  }, [fetchDevices, fetchGroupFiles, fetchGroupFolders, fetchGroups, fetchUsers, isAdmin, selectedGroup?.id, targetFolder]);

  const openEditModal = async () => {
    if (!selectedGroup) return;
    try {
      setLoading(true);
      const response = await authFetch(`/api/groups/${selectedGroup.id}`);
      if (!response.ok) throw new Error('Failed to fetch group details');
      const details = await response.json();
      setSelectedGroup(details);
      setShowEditModal(true);
    } catch (err) {
      console.error('Error loading group details:', err);
      setError('Failed to load group details');
    } finally {
      setLoading(false);
    }
  };

  // Create group
  const handleCreateGroup = async (groupData) => {
    try {
      setLoading(true);

      const payload = {
        ...groupData,
        ...(isAdmin && selectedUser && selectedUser !== user.username
          ? { owner_username: selectedUser }
          : {})
      };

      const response = await authFetch('/api/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        let serverMsg = '';
        try {
          const errPayload = await response.json();
          serverMsg = errPayload?.error || '';
        } catch {}
        const msg = response.status === 403 ? 'Access denied' : (serverMsg || 'Failed to create group');
        throw new Error(msg);
      }
      
      await fetchGroups();
      setShowCreateModal(false);
      setError(null);
    } catch (err) {
      console.error('Error creating group:', err);
      setError(err?.message || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  // Update group
  const handleUpdateGroup = async (groupId, groupData) => {
    try {
      setLoading(true);
      const response = await authFetch(`/api/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(groupData)
      });
      
      if (!response.ok) throw new Error('Failed to update group');
      
      await fetchGroups();
      if (selectedGroup && selectedGroup.id === groupId) {
        const response = await authFetch(`/api/groups/${groupId}`);
        const updated = await response.json();
        setSelectedGroup(updated);
      }
      setShowEditModal(false);
      setError(null);
    } catch (err) {
      console.error('Error updating group:', err);
      setError('Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  // Delete group
  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group? This cannot be undone.')) return;
    
    try {
      setLoading(true);
      const response = await authFetch(`/api/groups/${groupId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete group');
      
      if (selectedGroup && selectedGroup.id === groupId) {
        setSelectedGroup(null);
      }
      await fetchGroups();
      setError(null);
    } catch (err) {
      console.error('Error deleting group:', err);
      setError('Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

// Drag & Drop handlers
const handleDragEnter = useCallback((e) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragging(true);
}, []);

const handleDragOver = useCallback((e) => {
  e.preventDefault();
  e.stopPropagation();
}, []);

const handleDragLeave = useCallback((e) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.currentTarget === e.target) {
    setIsDragging(false);
  }
}, []);

const handleDrop = useCallback((e) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragging(false);

  if (!selectedGroup) {
    alert('Please select a group first');
    return;
  }

  const files = Array.from(e.dataTransfer.files);
  if (files.length > 0) {
    handleUploadToGroup(files);
  }
}, [selectedGroup]);

  // Upload to group
  const handleUploadToGroup = async (files) => {
    if (!selectedGroup || !files || files.length === 0) return;

    // Warn for very large batches so the user knows it'll take time
    const deviceCount = selectedGroup?.device_count || 0;
    const totalOps = files.length * deviceCount;
    if (totalOps > 200) {
      const ok = window.confirm(
        `You are uploading ${files.length} file(s) to ${deviceCount} device(s) — ${totalOps} total file operations.\n\n` +
        `The server will process these in parallel. This may take a few minutes.\n\nContinue?`
      );
      if (!ok) return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);

      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }
      if (targetFolder) {
        formData.append('targetFolder', targetFolder);
      }

      console.log(`Uploading ${files.length} file(s) to group ${selectedGroup.id}`);

      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/groups/${selectedGroup.id}/upload`, true);
        xhr.withCredentials = true;

        const token = localStorage.getItem('token');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        // Phase 1 — data transfer to server (0→99%)
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;
          // Reserve last 1% for server-side processing phase
          const percent = Math.min(99, Math.round((event.loaded / event.total) * 99));
          setUploadProgress(percent);
        };

        // Phase 2 — data transfer done; server is now pushing to devices
        xhr.upload.onload = () => {
          setUploadProgress(99); // stays at 99% until server responds
        };

        xhr.onload = () => {
          try {
            const payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            if (xhr.status >= 200 && xhr.status < 300) resolve(payload);
            else reject(new Error(payload?.error || payload?.details || `Server returned ${xhr.status}`));
          } catch (e) {
            reject(new Error(`Server returned ${xhr.status}`));
          }
        };

        xhr.onerror   = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Upload timed out — try fewer files or contact admin'));
        xhr.timeout   = 1800000; // 30 minutes — matches server-side timeout

        xhr.send(formData);
      });

      console.log('Upload result:', result);
      setUploadProgress(100);

      let message = `✅ Upload complete!\nSuccess: ${result.success}/${result.total} devices`;
      if (result.failed > 0) {
        message += `\n❌ Failed: ${result.failed} device(s)`;
        if (result.errors?.length > 0) {
          message += `\n\nErrors:\n${result.errors.slice(0, 5).join('\n')}`;
          if (result.errors.length > 5) message += `\n…and ${result.errors.length - 5} more`;
        }
      }
      alert(message);

      await fetchGroupFiles(selectedGroup.id, targetFolder);

    } catch (err) {
      console.error('Upload error:', err);
      setError(`Upload failed: ${err.message}`);
      alert(`Upload failed: ${err.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Delete from group
  const handleDeleteFromGroup = async () => {
    if (!selectedGroup || selectedFiles.length === 0) return;
    
    const fileNames = selectedFiles.map(f => f.name);
    const totalDevices = selectedFiles.reduce((sum, f) => sum + f.count, 0);
    
    if (!window.confirm(`Delete ${fileNames.length} file(s) from ${totalDevices} device(s)?`)) return;

    try {
      setIsDeleting(true);
      setDeleteStatus(`Deleting ${fileNames.length} file${fileNames.length > 1 ? 's' : ''} from ${totalDevices} device${totalDevices > 1 ? 's' : ''}…`);
      setLoading(true);
      const response = await authFetch(`/api/groups/${selectedGroup.id}/files`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files: fileNames })
      });

      if (!response.ok) throw new Error('Delete failed');

      const result = await response.json();
      setDeleteStatus(`Done! Deleted from ${result.success}/${result.total} devices`);
      await new Promise(r => setTimeout(r, 800)); // brief "Done" flash

      setSelectedFiles([]);
      await fetchGroupFiles(selectedGroup.id, targetFolder);
      setError(null);
    } catch (err) {
      console.error('Error deleting from group:', err);
      setError('Delete failed');
    } finally {
      setIsDeleting(false);
      setDeleteStatus('');
      setLoading(false);
    }
  };

  // File selection
  const handleFileSelect = (file) => {
    setSelectedFiles(prev => {
      const exists = prev.find(f => f.name === file.name);
      if (exists) {
        return prev.filter(f => f.name !== file.name);
      } else {
        return [...prev, file];
      }
    });
  };

  const handleSelectAllFiles = () => {
    if (selectedFiles.length === groupFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles([...groupFiles]);
    }
  };

  // Format file size
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileType = (name) => {
    const ext = (name || '').split('.').pop()?.toLowerCase();
    if (!ext || ext === name) return 'file';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'image';
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'aac', 'ogg', 'm4a'].includes(ext)) return 'audio';
    if (['pdf'].includes(ext)) return 'pdf';
    if (['txt', 'log', 'json', 'xml', 'csv'].includes(ext)) return 'text';
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive';
    return 'file';
  };

  const getFileIcon = (name) => {
    const t = getFileType(name);
    if (t === 'image') return '🖼️';
    if (t === 'video') return '🎞️';
    if (t === 'audio') return '🎵';
    if (t === 'pdf') return '📄';
    if (t === 'text') return '📝';
    if (t === 'archive') return '🗜️';
    return '📄';
  };

  const getThumbnailDeviceName = (file) => {
    const list = file?.devices;
    if (Array.isArray(list) && list.length > 0) return list[0];
    return null;
  };

  const sortedGroupFiles = React.useMemo(() => {
    const items = Array.isArray(groupFiles) ? [...groupFiles] : [];
    const dir = sortDirection === 'desc' ? -1 : 1;
    return items.sort((a, b) => {
      if (sortField === 'count') return ((a.count || 0) - (b.count || 0)) * dir;
      if (sortField === 'size') return ((a.size || 0) - (b.size || 0)) * dir;
      return String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase()) * dir;
    });
  }, [groupFiles, sortDirection, sortField]);

  const toggleSort = (field) => {
    setSortField(prevField => {
      if (prevField !== field) {
        setSortDirection('asc');
        return field;
      }
      setSortDirection(prevDir => (prevDir === 'asc' ? 'desc' : 'asc'));
      return prevField;
    });
  };

  const applyFolder = () => {
    setTargetFolder(pendingTargetFolder);
  };

  // Toggle device in modal
  const toggleDevice = (deviceName) => {
    setModalFormData(prev => ({
      ...prev,
      devices: prev.devices.includes(deviceName)
        ? prev.devices.filter(d => d !== deviceName)
        : [...prev.devices, deviceName]
    }));
  };

  // Handle modal submit
  const handleModalSubmit = (e, isEdit) => {
    e.preventDefault();
    if (isEdit) {
      handleUpdateGroup(selectedGroup.id, modalFormData);
    } else {
      handleCreateGroup(modalFormData);
    }
  };

  const getGroupOnlineOfflineCounts = useCallback((group) => {
    const deviceNames = group?.devices;
    if (!Array.isArray(deviceNames) || deviceNames.length === 0) {
      return { online: 0, offline: 0, total: 0 };
    }

    const deviceSet = new Set(deviceNames);
    const groupDeviceRows = availableDevices.filter(d => deviceSet.has(d.device_name));
    const online = groupDeviceRows.filter(d => d.is_online).length;
    const total = deviceNames.length;
    const offline = Math.max(0, total - online);
    return { online, offline, total };
  }, [availableDevices]);

  // Render group list
  const renderGroupList = () => (
    <div className="gm-sidebar">
      <div className="gm-sidebar-header">
        <h3>Groups</h3>
      </div>

      <button 
        className="gm-btn gm-btn-primary gm-create-btn"
        onClick={() => setShowCreateModal(true)}
      >
        + New Group
      </button>

      <div className="gm-group-list">
        {groups.map(group => (
          <div
            key={group.id}
            className={`gm-group-item ${selectedGroup?.id === group.id ? 'active' : ''}`}
            onClick={() => handleGroupClick(group)}
          >
            {(() => {
              const counts = getGroupOnlineOfflineCounts(group);
              return (
            <div className="gm-group-info">
              <div className="gm-group-name">{group.name}</div>
              <div className="gm-group-count-row">
                <div className="gm-group-count">{group.device_count} devices</div>
                <div className="gm-group-online-row">
                  <span className="gm-group-online">
                    <span className="gm-status-dot online" /> {counts.online} online
                  </span>
                  <span className="gm-group-offline">
                    <span className="gm-status-dot offline" /> {counts.offline} offline
                  </span>
                </div>
              </div>
            </div>
              );
            })()}
          </div>
        ))}
        {groups.length === 0 && !loading && (
          <div className="gm-empty">No groups yet</div>
        )}

        {/* 2026-05-18 — Edit / Delete / Control sit directly after the last
            group rather than at the bottom of the sidebar. Owner ask: keep
            actions reachable without scrolling past the right-panel's media
            list when there are many files in the selected group. */}
        {selectedGroup && (
          <div className="gm-sidebar-actions gm-sidebar-actions-inline">
            <button
              className="gm-btn gm-btn-secondary"
              onClick={openEditModal}
            >
              Edit
            </button>
            <button
              className="gm-btn gm-btn-danger"
              onClick={() => handleDeleteGroup(selectedGroup.id)}
            >
              Delete
            </button>
            <button
              className="gm-btn gm-btn-secondary"
              onClick={() => setShowGroupControlModal(true)}
            >
              Control
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Render group details
  const renderGroupDetails = () => {
    if (!selectedGroup) {
      return (
        <div className="gm-main">
          <div className="gm-empty-state">
            <h2>Select a group</h2>
            <p>Choose a group from the list to view files and devices</p>
          </div>
        </div>
      );
    }

    return (
      <div className="gm-main">
        <div className="gm-main-header">
          <h2>{selectedGroup.name}</h2>
          {selectedGroup.description && (
            <p className="gm-description">{selectedGroup.description}</p>
          )}
        </div>

        <div className="gm-files-section">
          <div className="gm-toolbar">
            <div className="gm-toolbar-left">
              <label className="gm-select-all">
                <input
                  type="checkbox"
                  checked={sortedGroupFiles.length > 0 && selectedFiles.length === sortedGroupFiles.length}
                  onChange={handleSelectAllFiles}
                  disabled={sortedGroupFiles.length === 0}
                />
                <span>Select All</span>
              </label>
            </div>
            <div className="gm-toolbar-right">
              <div className="gm-toolbar-row gm-toolbar-row-top">
                <select
                  className="gm-folder-select"
                  value={pendingTargetFolder}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPendingTargetFolder(next);
                    setTargetFolder(next);
                  }}
                  disabled={!selectedGroup}
                  title="Working folder"
                >
                  <option value="">Root</option>
                  {groupFolders.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
                <button
                  className="gm-btn gm-btn-secondary"
                  onClick={applyFolder}
                  disabled={pendingTargetFolder === targetFolder}
                  title="Apply working folder"
                >
                  Apply
                </button>

                <div className="gm-view-toggle">
                  <button
                    className={`gm-btn gm-btn-secondary ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List view"
                  >
                    ☰
                  </button>
                  <button
                    className={`gm-btn gm-btn-secondary ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                  >
                    ⬚
                  </button>
                </div>
              </div>

              <div className="gm-toolbar-row gm-toolbar-row-bottom">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleUploadToGroup(Array.from(e.target.files))}
                />
                <button
                  className="gm-btn gm-btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  📤 Upload
                </button>
                <button
                  className="gm-btn gm-btn-danger"
                  onClick={handleDeleteFromGroup}
                  disabled={selectedFiles.length === 0}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>

          {isUploading && (
            <div className="gm-progress">
              <div className="gm-progress-bar">
                <div
                  className="gm-progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <div className="gm-progress-text">
                {uploadProgress < 99
                  ? `Uploading… ${uploadProgress}%`
                  : uploadProgress === 100
                    ? 'Done!'
                    : 'Distributing to devices… please wait'}
              </div>
            </div>
          )}

          {viewMode === 'list' ? (
  <div 
    className={`gm-file-list gm-file-list-v2 ${isDragging ? 'gm-dragging' : ''}`}
    onDragEnter={handleDragEnter}
    onDragOver={handleDragOver}
    onDragLeave={handleDragLeave}
    onDrop={handleDrop}
  >
    {isDragging && (
      <div className="gm-drop-overlay">
        <div className="gm-drop-message">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <h3>Drop files here</h3>
          <p>Upload to {selectedGroup?.name}</p>
        </div>
      </div>
    )}

    <div className="gm-file-header gm-file-header-v2">
      <div className="gm-col gm-col-check" />
      <div className="gm-col gm-col-name" onClick={() => toggleSort('name')} role="button" tabIndex={0}>
        Name
        <span className="gm-sort-indicator">{sortField === 'name' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</span>
      </div>
      <div className="gm-col gm-col-count" onClick={() => toggleSort('count')} role="button" tabIndex={0}>
        Copies
        <span className="gm-sort-indicator">{sortField === 'count' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</span>
      </div>
      <div className="gm-col gm-col-size" onClick={() => toggleSort('size')} role="button" tabIndex={0}>
        Size
        <span className="gm-sort-indicator">{sortField === 'size' ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}</span>
      </div>
    </div>

    {sortedGroupFiles.map(file => {
      const listDeviceName = getThumbnailDeviceName(file);
      const listFileType   = getFileType(file.name);
      const listCanThumb   = !!listDeviceName && (listFileType === 'image' || listFileType === 'video' || listFileType === 'pdf');
      const listThumbPath  = targetFolder ? `${targetFolder}/${file.name}` : file.name;

      return (
      <div key={file.name} className={`gm-file-row ${selectedFiles.some(f => f.name === file.name) ? 'selected' : ''}`}>
        <div className="gm-col gm-col-check">
          <input
            type="checkbox"
            checked={selectedFiles.some(f => f.name === file.name)}
            onChange={() => handleFileSelect(file)}
          />
        </div>
        <div className="gm-col gm-col-name" title={file.name}>
          <span className="gm-file-icon">
            {listCanThumb ? (
              <CachedGridThumbnail
                itemPath={listThumbPath}
                isVideo={listFileType === 'video'}
                isPdf={listFileType === 'pdf'}
                deviceName={listDeviceName}
                size="list"
                lazyLoad={true}
                key={`${listDeviceName}-${listThumbPath}`}
              />
            ) : (
              getFileIcon(file.name)
            )}
          </span>
          <span className="gm-file-name-text">{file.name}</span>
        </div>
        <div className="gm-col gm-col-count">
          <span className="gm-file-count">{file.count || 0}×</span>
        </div>
        <div className="gm-col gm-col-size">
          {formatBytes(file.size || 0)}
        </div>
      </div>
      );
    })}

    {sortedGroupFiles.length === 0 && !loading && (
      <div className="gm-empty">No files in this group</div>
    )}
  </div>
) : (
  <div 
    className={`gm-file-grid ${isDragging ? 'gm-dragging' : ''}`}
    onDragEnter={handleDragEnter}
    onDragOver={handleDragOver}
    onDragLeave={handleDragLeave}
    onDrop={handleDrop}
  >
    {isDragging && (
      <div className="gm-drop-overlay">
        <div className="gm-drop-message">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <h3>Drop files here</h3>
          <p>Upload to {selectedGroup?.name}</p>
        </div>
      </div>
    )}

    {sortedGroupFiles.map(file => {
      const isSelected = selectedFiles.some(f => f.name === file.name);
      const deviceNameForThumb = getThumbnailDeviceName(file);
      const fileType = getFileType(file.name);
      const showThumb = !!deviceNameForThumb && (fileType === 'image' || fileType === 'video' || fileType === 'pdf');
      const thumbItemPath = targetFolder ? `${targetFolder}/${file.name}` : file.name;
      return (
        <div
          key={file.name}
          className={`gm-file-card ${isSelected ? 'selected' : ''}`}
          title={file.name}
        >
          {/* Thumbnail area — click selects (primary action in GroupManager) */}
          <div
            className="gm-file-card-thumb"
            onClick={() => handleFileSelect(file)}
            role="button"
            tabIndex={0}
          >
            {showThumb ? (
              <CachedGridThumbnail
                itemPath={thumbItemPath}
                isVideo={fileType === 'video'}
                isPdf={fileType === 'pdf'}
                size="medium"
                deviceName={deviceNameForThumb}
                key={`${deviceNameForThumb}-${thumbItemPath}`}
              />
            ) : (
              <div className="gm-file-card-icon">{getFileIcon(file.name)}</div>
            )}
          </div>

          {/* Footer row — checkbox + name + meta, tap anywhere to select */}
          <div
            className="gm-file-card-footer"
            onClick={() => handleFileSelect(file)}
          >
            <span className={`gm-card-checkbox ${isSelected ? 'checked' : ''}`} aria-hidden="true" />
            <div className="gm-file-card-info">
              <div className="gm-file-card-name">{file.name}</div>
              <div className="gm-file-card-meta">
                <span className="gm-file-count">{file.count || 0}×</span>
                <span>{formatBytes(file.size || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      );
    })}
    {sortedGroupFiles.length === 0 && !loading && (
      <div className="gm-empty">No files in this group</div>
    )}
  </div>
)}

          <div className="gm-files-footer">
            <div className="gm-files-footer-left">
              Total files: {sortedGroupFiles.length}
            </div>
            <div className="gm-files-footer-right">
              Selected: {selectedFiles.length}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render create/edit modal
  const renderModal = (isEdit = false) => {
    return (
      <div className="gm-modal-overlay" onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}>
        <div className="gm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="gm-modal-header">
            <h2>{isEdit ? 'Edit Group' : 'Create Group'}</h2>
            <button 
              className="gm-modal-close"
              onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}
            >
              ✕
            </button>
          </div>

          <form onSubmit={(e) => handleModalSubmit(e, isEdit)} className="gm-modal-body">
            <div className="gm-form-group">
              <label>Group Name *</label>
              <input
                type="text"
                value={modalFormData.name}
                onChange={(e) => setModalFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="e.g., Cairo Stores"
              />
            </div>

            <div className="gm-form-group">
              <label>Description</label>
              <textarea
                value={modalFormData.description}
                onChange={(e) => setModalFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                rows="3"
              />
            </div>

            <div className="gm-form-group">
              <label>Devices ({modalFormData.devices.length} selected)</label>
              <div className="gm-device-list">
                {availableDevices.map(device => (
                  <div key={device.device_name} className="gm-device-item">
                    <input
                      type="checkbox"
                      checked={modalFormData.devices.includes(device.device_name)}
                      onChange={() => toggleDevice(device.device_name)}
                    />
                    <span>{device.device_name}</span>
                    <span className="gm-device-status" aria-label={device.is_online ? 'online' : 'offline'}>
                      <span className={`gm-status-dot ${device.is_online ? 'online' : 'offline'}`} />
                    </span>
                  </div>
                ))}
                {availableDevices.length === 0 && (
                  <div className="gm-empty">No devices available</div>
                )}
              </div>
            </div>

            <div className="gm-modal-footer">
              <button 
                type="button" 
                className="gm-btn gm-btn-secondary"
                onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="gm-btn gm-btn-primary"
                disabled={!modalFormData.name.trim() || modalFormData.devices.length === 0}
              >
                {isEdit ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Permission check - show access denied for regular users
  if (!hasAccess) {
    return (
      <div className="gm-page">
        <div className="gm-container">
          <div className="gm-header">
            <div className="gm-header-left">
              <h1>🖥️ Group Management</h1>
            </div>
            <button className="gm-close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="gm-access-denied">
            <div className="access-denied-content">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: '64px', height: '64px', marginBottom: '20px', color: '#f44336'}}>
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <h2>Access Denied</h2>
              <p>Groups Management is a <strong>Pro</strong> feature.</p>
              <p>Please upgrade to a Pro or Admin account to access this functionality.</p>
              <button onClick={onClose} className="gm-btn-primary" style={{marginTop: '20px'}}>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="gm-page">

      {/* Blocking overlay during delete */}
      {isDeleting && (
        <div className="gm-blocking-overlay">
          <div className="gm-blocking-box">
            <div className="gm-blocking-spinner" />
            <div className="gm-blocking-title">{deleteStatus || 'Deleting files…'}</div>
            <div className="gm-blocking-subtitle">Please wait — do not close this tab</div>
          </div>
        </div>
      )}

      <div className="gm-container">
        <div className="gm-header">
          <div className="gm-header-left">
            <h1>🖥️ Group Management</h1>

            {isAdmin && (
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="gm-user-select"
              >
                <option value={user.username}>My Groups</option>
                {selectableUsers.map(u => (
                  <option key={u.username} value={u.username}>{u.username}</option>
                ))}
              </select>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              className="gm-btn gm-btn-secondary"
              onClick={refreshAll}
              disabled={loading}
              title="Refresh all data"
            >
              🔄 Refresh All
            </button>
          </div>
        </div>

        {error && (
          <div className="gm-error">
            {error}
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {loading && <div className="gm-loading">Loading...</div>}

        <div className="gm-content">
          {renderGroupList()}
          {renderGroupDetails()}
        </div>

        {showCreateModal && renderModal(false)}
        {showEditModal && renderModal(true)}

        {showGroupControlModal && selectedGroup && (
          <GroupControlModal
            groups={groups}
            initialGroupId={selectedGroup.id}
            onClose={() => setShowGroupControlModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default GroupManager;
